import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeDollarSign, Paperclip, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Pending client-reported payments for one invoice, with Accept / Dismiss. */
export function PaymentClaimsPanel({ invoiceId }) {
  const qc = useQueryClient();
  const [dismissing, setDismissing] = useState(null);
  const [reason, setReason] = useState("");
  const [notifyClient, setNotifyClient] = useState(false);
  const { data: claims = [] } = useQuery({
    queryKey: ["payment-claims", invoiceId],
    queryFn: () => api.listPaymentClaims({ invoice_id: invoiceId, status: "pending" }),
    enabled: !!invoiceId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["payment-claims"] });
    qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoice-payments", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoices"] });
  };
  const acceptMut = useMutation({
    mutationFn: (id) => api.acceptPaymentClaim(id),
    onSuccess: (res) => {
      const n = res.recorded.length;
      toast.success(n ? `Payment recorded on ${n} invoice${n === 1 ? "" : "s"}` : "Nothing left to record");
      if (res.client_notified) toast.info(`Thank-you email sent to ${res.client_notified}`);
      if (res.skipped?.length) toast.info(res.skipped.join("; "));
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Accept failed"),
  });
  const dismissMut = useMutation({
    mutationFn: ({ id, reason: r, notify }) => api.dismissPaymentClaim(id, { reason: r, notify_client: notify }),
    onSuccess: (res) => {
      toast.success(res.client_notified ? `Report dismissed — client notified at ${res.client_notified}` : "Report dismissed");
      setDismissing(null); setReason(""); setNotifyClient(false); refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Dismiss failed"),
  });

  if (!claims.length) return null;
  return (
    <div className="border border-amber-300 rounded-md bg-amber-50/60 mb-5" data-testid="payment-claims-panel">
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <BadgeDollarSign className="size-4 text-amber-700" />
        <p className="overline text-amber-800">Client reported payment</p>
        <span className="ml-auto text-[11px] text-amber-800/80">{claims.length} awaiting review</span>
      </div>
      <ul className="divide-y divide-amber-200">
        {claims.map((c) => {
          const mine = c.allocations.find((a) => a.invoice_id === invoiceId);
          return (
            <li key={c.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`payment-claim-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">
                  {fmtMoney(mine?.amount ?? c.amount)} {c.currency}
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    paid {fmtDate(c.paid_on)} · {c.method.replace("_", " ")}
                    {c.reference ? ` · ref ${c.reference}` : ""}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Reported {fmtDate(c.created_at.slice(0, 10))}
                  {c.reporter_name ? ` by ${c.reporter_name}` : ""}
                  {c.allocations.length > 1 && ` · covers ${c.allocations.map((a) => a.invoice_number).join(", ")}`}
                  {c.note ? ` — “${c.note}”` : ""}
                </div>
              </div>
              {c.proof && (
                <Button variant="outline" size="sm" className="bg-white" data-testid={`claim-proof-${c.id}`}
                        onClick={() => api.openPaymentClaimProof(c.id)}>
                  <Paperclip className="size-3.5 mr-1" /> Proof
                </Button>
              )}
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`claim-accept-${c.id}`}
                      disabled={acceptMut.isPending} onClick={() => acceptMut.mutate(c.id)}>
                <Check className="size-3.5 mr-1" /> Accept &amp; record
              </Button>
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`claim-dismiss-${c.id}`}
                      onClick={() => setDismissing(c)}>
                <X className="size-3.5 mr-1" /> Dismiss
              </Button>
            </li>
          );
        })}
      </ul>

      <Dialog open={!!dismissing} onOpenChange={(v) => !v && setDismissing(null)}>
        <DialogContent data-testid="claim-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Dismiss this payment report?</DialogTitle>
            <DialogDescription>No payment will be recorded. The client is not notified automatically.</DialogDescription>
          </DialogHeader>
          <Input data-testid="claim-dismiss-reason" placeholder="Reason (optional) — e.g. no funds received"
                 value={reason} onChange={(e) => setReason(e.target.value)} />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="claim-dismiss-notify" checked={notifyClient} onCheckedChange={(v) => setNotifyClient(!!v)} />
            Notify client by email — “we couldn&apos;t match this payment, please check” (includes the reason)
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissing(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="claim-dismiss-confirm"
                    disabled={dismissMut.isPending}
                    onClick={() => dismissMut.mutate({ id: dismissing.id, reason, notify: notifyClient })}>
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
